import React, { forwardRef, useState, useEffect, useRef, useImperativeHandle } from 'react';
import { clsx } from 'clsx';
import { NumericKeyboard } from './NumericKeyboard';
import { AlphaNumericKeyboard } from './AlphaNumericKeyboard';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'touch';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftAddon?: string;
  rightAddon?: string;
  useCustomKeyboard?: boolean;
  keyboardType?: 'numeric' | 'alphanumeric';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      className,
      fullWidth = false,
      size = 'md',
      leftIcon,
      rightIcon,
      leftAddon,
      rightAddon,
      type = 'text',
      useCustomKeyboard = false,
      keyboardType = 'numeric',
      ...props
    },
    ref
  ) => {
    const [showKeyboard, setShowKeyboard] = useState(false);
    const [inputValue, setInputValue] = useState(props.value || '');
    const inputRef = useRef<HTMLInputElement>(null);
    
    // Exponer el inputRef a través del forwardRef
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    
    // Update internal state when props.value changes
    useEffect(() => {
      if (props.value !== undefined) {
        setInputValue(props.value);
      }
    }, [props.value]);
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      
      // Call the original onChange handler if provided
      if (props.onChange) {
        props.onChange(e);
      }
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (useCustomKeyboard) {
        setShowKeyboard(true);
      }
      
      // Call the original onFocus handler if provided
      if (props.onFocus) {
        props.onFocus(e);
      }
    };
    
    const handleKeyPress = (key: string) => {
      if (!inputRef.current) return;
      
      const input = inputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      
      // Create a new value by inserting the key at the cursor position
      const newValue = 
        (inputValue as string).substring(0, start) + 
        key + 
        (inputValue as string).substring(end);
      
      // Update the input value
      setInputValue(newValue);
      
      // Create a synthetic event to trigger onChange
      const event = {
        target: {
          value: newValue,
          name: props.name
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      if (props.onChange) {
        props.onChange(event);
      }
      
      // Set cursor position after the inserted character
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = start + 1;
          inputRef.current.selectionEnd = start + 1;
          inputRef.current.focus();
        }
      }, 0);
    };
    
    const handleDeleteKey = () => {
      if (!inputRef.current) return;
      
      const input = inputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      
      if (start === end && start > 0) {
        // If no text is selected and cursor is not at the beginning
        const newValue = 
          (inputValue as string).substring(0, start - 1) + 
          (inputValue as string).substring(end);
        
        setInputValue(newValue);
        
        // Create a synthetic event to trigger onChange
        const event = {
          target: {
            value: newValue,
            name: props.name
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        if (props.onChange) {
          props.onChange(event);
        }
        
        // Set cursor position
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.selectionStart = start - 1;
            inputRef.current.selectionEnd = start - 1;
            inputRef.current.focus();
          }
        }, 0);
      } else if (start !== end) {
        // If text is selected
        const newValue = 
          (inputValue as string).substring(0, start) + 
          (inputValue as string).substring(end);
        
        setInputValue(newValue);
        
        // Create a synthetic event to trigger onChange
        const event = {
          target: {
            value: newValue,
            name: props.name
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        if (props.onChange) {
          props.onChange(event);
        }
        
        // Set cursor position
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.selectionStart = start;
            inputRef.current.selectionEnd = start;
            inputRef.current.focus();
          }
        }, 0);
      }
    };
    
    const handleClearKey = () => {
      setInputValue('');
      
      // Create a synthetic event to trigger onChange
      const event = {
        target: {
          value: '',
          name: props.name
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      if (props.onChange) {
        props.onChange(event);
      }
      
      // Set focus back to input
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
      touch: 'px-4 py-4 text-xl min-h-14', // Optimizado para pantallas táctiles
    };

    const id = React.useId();

    return (
      <div className={clsx('flex flex-col', fullWidth ? 'w-full' : '', className)}>
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              'mb-1 font-medium',
              error ? 'text-red-600' : 'text-gray-700',
              size === 'sm' ? 'text-sm' : '',
              size === 'lg' || size === 'touch' ? 'text-lg' : ''
            )}
          >
            {label}
          </label>
        )}

        <div className="relative flex" onClick={() => { 
          if (useCustomKeyboard && inputRef.current) {
            inputRef.current.focus();
            setShowKeyboard(true);
          }
        }}>
          {leftAddon && (
            <div className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
              {leftAddon}
            </div>
          )}

          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}

          <input
            ref={inputRef}
            id={id}
            type={type}
            className={clsx(
              'block border focus:ring-2 focus:outline-none transition-colors',
              sizes[size],
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
              leftIcon ? 'pl-10' : '',
              rightIcon ? 'pr-10' : '',
              leftAddon ? 'rounded-r-md rounded-l-none' : rightAddon ? 'rounded-l-md rounded-r-none' : 'rounded-md',
              fullWidth ? 'w-full' : ''
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : helperText ? `${id}-helper` : undefined}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            readOnly={useCustomKeyboard && showKeyboard}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}

          {rightAddon && (
            <div className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-100 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
              {rightAddon}
            </div>
          )}
        </div>

        {error && (
          <p id={`${id}-error`} className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={`${id}-helper`} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
        
        {useCustomKeyboard && showKeyboard && (
          keyboardType === 'numeric' ? (
            <NumericKeyboard
              onKeyPress={handleKeyPress}
              onDelete={handleDeleteKey}
              onClear={handleClearKey}
            />
          ) : (
            <AlphaNumericKeyboard
              onKeyPress={handleKeyPress}
              onDelete={handleDeleteKey}
              onClear={handleClearKey}
            />
          )
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
